import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined, SearchOutlined } from "@mui/icons-material";
import { AdminFolderAccess } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import {
  useGetAdminUserFoldersQuery,
  useGetAssignableFoldersQuery,
} from "features/adminUsers/adminUsers";

interface Props {
  userId: number | null;
  isCreateMode?: boolean;
  isDataStorageUser: boolean;
  canEdit: boolean;
  isCalibrateManager: boolean;
  folderDraft?: Record<number, boolean>;
  onFolderDraftChange?: (folderId: number, hasAccess: boolean, serverHasAccess: boolean) => void;
  onSaveSuccess?: (message: string, severity: "success" | "error") => void;
  onSaveComplete?: () => void;
  onSaveAll?: () => void;
  onCreateUser?: () => void;
  createUserDisabled?: boolean;
  hasAnyChanges?: boolean;
  isSaving?: boolean;
}

function getFolderDisplayName(folder: AdminFolderAccess): string {
  if (folder.folder_display != null && folder.folder_display !== "") {
    return folder.folder_display;
  }
  const isMigration = !folder.is_integration && !folder.is_sftp_folder;
  const ownerName = (folder.owner_first_name || "").trim();
  if (isMigration && ownerName) {
    return `${ownerName} - ${folder.folder}`;
  }
  return folder.folder;
}

function FolderRow({
  folder,
  checked,
  disabled,
  onToggle,
}: {
  folder: AdminFolderAccess;
  checked: boolean;
  disabled: boolean;
  onToggle: (folder: AdminFolderAccess) => void;
}) {
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      py={0.6}
      px={0.5}
      sx={{
        borderRadius: 1,
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Checkbox
          checked={checked}
          size="small"
          disableRipple
          disabled={disabled}
          onChange={() => onToggle(folder)}
          sx={{ p: 0 }}
        />
      </Box>
      <Box flex={1} minWidth={0}>
        <Tooltip title={getFolderDisplayName(folder)} enterDelay={300} placement="top-start">
          <Typography variant="body2" fontWeight={600} noWrap component="span" display="block">
            {getFolderDisplayName(folder)}
          </Typography>
        </Tooltip>
      </Box>
      <Box display="flex" gap={0.5} flexShrink={0}>
        {folder.is_sftp_folder ? (
          <Chip label="SFTP" size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "rgba(21,101,192,0.10)", color: "#1565c0" }} />
        ) : folder.is_integration ? (
          <Chip label="Integration" size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100" }} />
        ) : (
          <Chip label="Migration" size="small" sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "#fff8e1", color: "#ff8f00" }} />
        )}
        {folder.sync_status !== "ready" && (
          <Chip
            label={folder.sync_status}
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: folder.sync_status === "syncing" ? "rgba(21,101,192,0.10)" : "rgba(211,47,47,0.10)",
              color: folder.sync_status === "syncing" ? "#1565c0" : "error.dark",
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default function UserManagementOwnedFoldersTab({
  userId,
  isCreateMode = false,
  isDataStorageUser,
  canEdit,
  isCalibrateManager,
  folderDraft,
  onFolderDraftChange,
  onSaveSuccess,
  onSaveComplete,
  onSaveAll,
  onCreateUser,
  createUserDisabled = false,
  hasAnyChanges = false,
  isSaving = false,
}: Props) {
  const { data: userFolders, isLoading: isLoadingUser } = useGetAdminUserFoldersQuery(userId!, { skip: !userId });
  const { data: assignableFolders, isLoading: isLoadingAssignable } = useGetAssignableFoldersQuery(undefined, { skip: !isCreateMode });

  const folders = isCreateMode ? assignableFolders : userFolders;
  const isLoading = isCreateMode ? isLoadingAssignable : isLoadingUser;
  const [search, setSearch] = useState("");

  const readOnly = isCalibrateManager && !canEdit;

  const getChecked = (folder: AdminFolderAccess) =>
    folderDraft?.[folder.id] ?? folder.has_access === true;

  const handleToggle = (folder: AdminFolderAccess) => {
    if (readOnly) return;
    const next = !getChecked(folder);
    const serverHasAccess = folder.has_access === true;
    onFolderDraftChange?.(folder.id, next, serverHasAccess);
  };

  const list = folders ?? [];
  const hasUnsaved = list.some((f) => (folderDraft?.[f.id] ?? f.has_access === true) !== (f.has_access === true));

  if (!isDataStorageUser) {
    return (
      <Box px={2.5} pt={3} display="flex" flexDirection="column" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          Enable Data Storage User role to manage folder access.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box px={2.5} pt={2}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="text" height={40} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  const filtered = search.trim()
    ? list.filter(
        (f) =>
          getFolderDisplayName(f).toLowerCase().includes(search.toLowerCase()) ||
          f.folder_prefix.toLowerCase().includes(search.toLowerCase())
      )
    : list;

  // Order by server-side access (has_access) so changes in the UI
  // don't re-order rows until after a successful save/refetch.
  const ordered = [...filtered].sort((a, b) => {
    const aServer = a.has_access === true;
    const bServer = b.has_access === true;
    if (aServer === bServer) return 0;
    return aServer ? -1 : 1;
  });

  return (
    <>
      <Box px={2} pt={1} pb={0.5} display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" color="text.secondary">
          Check the box to grant this user access to a folder.
        </Typography>
        <Tooltip title="Checked folders will be visible to the user on the web platform.">
          <InfoOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
        </Tooltip>
      </Box>
      <Box px={2} pt={0.5} pb={1}>
        <TextField
          size="small"
          placeholder="Search folders..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          inputProps={{ autoComplete: "off" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
        />
      </Box>
      <CustomScrollbars sx={{ flex: 1, minHeight: 0, px: 2, pb: 1 }}>
        {ordered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
            {list.length === 0 ? "No folders available." : "No folders match your search."}
          </Typography>
        ) : (
          ordered.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              checked={getChecked(folder)}
              disabled={readOnly || !canEdit}
              onToggle={handleToggle}
            />
          ))
        )}
      </CustomScrollbars>
      <Box px={2} pb={2} pt={1} sx={{ flexShrink: 0 }}>
        {hasAnyChanges && (
          <Typography
            variant="caption"
            fontWeight={600}
            display="block"
            sx={{ mb: 1, color: "#ad1457" }}
          >
            Unsaved changes
          </Typography>
        )}
        {isCreateMode && onCreateUser ? (
          <Button
            variant="contained"
            fullWidth
            onClick={onCreateUser}
            disabled={createUserDisabled || isSaving}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
          >
            {isSaving ? "Creating…" : "Create User"}
          </Button>
        ) : (
          !isCreateMode &&
          onSaveAll && (
            <Tooltip title={!hasAnyChanges ? "No changes to save" : ""}>
              <span>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onSaveAll}
                  disabled={!canEdit || !hasAnyChanges || isSaving}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                >
                  {isSaving ? "Saving…" : "Save Changes"}
                </Button>
              </span>
            </Tooltip>
          )
        )}
      </Box>
    </>
  );
}
